// DriftBars (TASK-496) — ported from SixEase widgets/DriftBars.tsx, re-grounded on
// Kielis portfolioApiService.getDrift and re-tokenised to shadcn tokens. SixEase read a
// single `sac_rows[]`; Kielis's drift_by_asset_class is keyed by sub-asset-class with
// current/target/drift (percent) + a status — we derive `breach` from status ('on_target'
// ⇔ in band). Diverging bars around a center tick, ±tolerance_pct mandate band highlighted
// (per-mandate from the endpoint; 2.0 only as fallback).
import { AlertTriangle, Check } from 'lucide-react';
import { useDrift } from '../../../hooks/useDrift';
import type { SignalScope } from '../../../types/signalScope';
import { useCioCandidates } from '../../../hooks/useCio';
import { WidgetContainer } from '../WidgetContainer';
import { OpenAsSpreadsheet } from './OpenAsSpreadsheet';

const DEFAULT_TOLERANCE = 2.0; // fallback only — the drawn band uses the endpoint's per-mandate tolerance_pct

interface DriftRow {
  sub_asset_class: string;
  current_pct: number;
  target_pct: number;
  drift_pp: number;
  breach: boolean;
}

function DriftBar({
  row, maxDrift, tolerance, focused = false,
}: { row: DriftRow; maxDrift: number; tolerance: number; focused?: boolean }) {
  const scale = maxDrift > 0 ? maxDrift : tolerance;
  const halfWidth = 50; // each side is 50% of the bar track
  const fillPct = Math.min(Math.abs(row.drift_pp) / scale, 1) * halfWidth;
  const isLeft = row.drift_pp < 0;
  const thresholdPct = Math.min(tolerance / scale, 1) * halfWidth;

  return (
    <div
      data-testid={focused ? 'drift-focused-sac' : undefined}
      className={`mb-3 last:mb-0 ${focused ? '-mx-2 rounded-md bg-primary/5 px-2 py-1.5 ring-1 ring-primary' : ''}`}
    >
      <div className="mb-0.5 flex items-center justify-between">
        <span className="max-w-[55%] truncate text-[12px] font-medium text-foreground">
          {focused && (
            <span className="mr-1 rounded bg-primary px-1 py-0.5 text-[9px] font-semibold text-primary-foreground">
              This signal
            </span>
          )}
          {row.sub_asset_class}
        </span>
        <div className="flex shrink-0 items-center gap-2">
          <span className="font-mono text-[11px] text-muted-foreground">
            {row.current_pct.toFixed(1)}% / {row.target_pct.toFixed(1)}%
          </span>
          <span
            className={`inline-flex items-center gap-1 font-mono text-[11px] font-semibold ${
              row.breach ? 'text-destructive' : 'text-emerald-600'
            }`}
          >
            {row.breach && <AlertTriangle className="h-3 w-3" aria-label="drift breach" />}
            {row.drift_pp > 0 ? '+' : ''}
            {row.drift_pp.toFixed(1)}pp
          </span>
        </div>
      </div>
      <div className="relative h-2 rounded-full bg-accent">
        {/* ±tolerance band highlight (per-mandate, from the drift endpoint) */}
        <div
          className="absolute top-0 h-full rounded-full bg-muted opacity-80"
          style={{ left: `${halfWidth - thresholdPct}%`, width: `${thresholdPct * 2}%` }}
        />
        {/* center tick */}
        <div className="absolute top-0 h-full w-px bg-border" style={{ left: '50%' }} />
        {/* fill */}
        <div
          className={`absolute top-0 h-full rounded-full ${row.breach ? 'bg-destructive' : 'bg-emerald-500'}`}
          style={{ left: isLeft ? `${halfWidth - fillPct}%` : '50%', width: `${fillPct}%` }}
        />
      </div>
    </div>
  );
}

/**
 * TASK-832: DriftBars, opened on the breach the advisor clicked.
 *
 * "Open drift analysis" did not open drift analysis. `AlertGroupCard`'s `CAUSE_ACTIONS` routed
 * it to `/clients/{id}?tab=portfolios` — **the same generic Portfolios tab "Open portfolio"
 * opens from a different group**. Two differently-labelled buttons, one URL. No target bands,
 * no drift view, no anchor to the breaching allocations the card had just named.
 *
 * This widget already drew exactly the right picture — current vs target per sub-asset class,
 * diverging bars around a centre tick, the per-mandate band from the endpoint. It was
 * client-scoped, unfocused, actionless, and never linked from the alert that needed it.
 *
 * The alert carries everything needed to focus it: `portfolio_drift_breach` emits
 * `sub_asset_class`, `current`, `target`, `drift` and `tolerance_breach` in `event_metadata`
 * (`portfolio-service/main.py:1216-1223`).
 */
/**
 * TASK-832: what the mandate says to do about this breach, sized against the gap.
 *
 * **Sizing a trade is not placing one.** `BeforeAfter` already disclaims this correctly and
 * its wording is the precedent copied here — a proposal that reads like an instruction is
 * worse than no proposal.
 *
 * The catalogue requires a written rationale for `propose_trade`
 * (`rationale_required_for=("propose_trade",)`), so this widget deliberately offers no
 * one-click "propose" that would bypass that seam. It states the gap and names candidates;
 * committing to a trade goes through the disposition path that collects the reason.
 */
function DriftProposal({
  clientId, row, tolerance,
}: { clientId: string; row: DriftRow; tolerance: number }) {
  const underweight = row.drift_pp < 0;
  // CIO BUY candidates in the SAC that is short. Only meaningful when the breach is a
  // shortfall — an overweight is corrected by trimming what is held, not by buying more.
  const { data, isLoading } = useCioCandidates(
    underweight ? { sac: row.sub_asset_class, clientId } : {},
  );
  const candidates = (data?.candidates ?? []).filter((c) => c.clears_exclusions !== false);

  return (
    <div data-testid="drift-proposal" className="mt-3 rounded-md border border-border bg-accent/40 p-3">
      <p className="text-[12px] font-semibold text-foreground">
        {underweight ? 'Under target' : 'Over target'} by {Math.abs(row.drift_pp).toFixed(1)}pp
        <span className="ml-1 font-normal text-muted-foreground">
          (band ±{tolerance}pp · target {row.target_pct.toFixed(1)}% · now {row.current_pct.toFixed(1)}%)
        </span>
      </p>

      {underweight ? (
        <div className="mt-2">
          <p className="text-[11px] text-muted-foreground">
            CIO BUY candidates in this sub-asset class that clear this client&apos;s exclusions:
          </p>
          {isLoading && <div className="mt-1 h-4 w-40 animate-pulse rounded bg-accent" />}
          {!isLoading && candidates.length === 0 && (
            // The same "no compliant option" framing TASK-830 uses: an absence with a reason
            // beats an empty list.
            <p data-testid="drift-no-candidates" className="mt-1 text-[11px] text-muted-foreground">
              None — no CIO BUY in this bucket clears the client&apos;s exclusions, so the gap
              cannot be closed from the house list alone.
            </p>
          )}
          <ul className="mt-1 space-y-0.5">
            {candidates.slice(0, 5).map((c) => (
              <li key={c.isin} data-testid="drift-candidate" className="text-[11px] text-foreground">
                {c.issuer ?? c.isin}
                {c.fit_gain != null && (
                  <span className="ml-1 font-mono text-emerald-600">
                    +{Math.round(c.fit_gain * 100)}pp fit
                  </span>
                )}
                {c.cio_view && <span className="ml-1 text-muted-foreground">· {c.cio_view}</span>}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="mt-2 text-[11px] text-muted-foreground">
          Correcting this means trimming holdings in this sub-asset class — the house BUY list
          is not the instrument for an overweight.
        </p>
      )}

      {/* TASK-657 / TASK-832: recommendation ≠ execution, in the same words BeforeAfter uses. */}
      <p data-testid="drift-not-an-order" className="mt-2 border-t border-border pt-2 text-[11px] text-muted-foreground">
        This is a recommendation only — no orders are placed. The mandate&apos;s target weights
        define the correction; committing to a trade records a written rationale first.
      </p>
    </div>
  );
}

export function DriftBars({ clientId, focusSac }: SignalScope) {
  const { data, isLoading, isError, error } = useDrift(clientId);

  const dba = data?.drift?.drift_by_asset_class ?? {};
  const rows: DriftRow[] = Object.entries(dba).map(([sub, v]) => ({
    sub_asset_class: sub,
    current_pct: v.current,
    target_pct: v.target,
    drift_pp: v.drift,
    breach: v.status !== 'on_target',
  }));
  const tolerance = data?.drift?.tolerance_pct ?? DEFAULT_TOLERANCE;

  return (
    // TASK-825: the drift calculation's own timestamp, not the tile's mount clock.
    <WidgetContainer
      source="Portfolio · Drift"
      dataAsOf={data?.timestamp ?? null}
      // TASK-876: `Open as spreadsheet` is the handover primitive, and it was mounted on
      // two widgets out of the whole product. The drift table is the one an advisor is
      // most likely to send to a colleague with "check the third row".
      toolbar={
        rows.length > 0 ? (
          <OpenAsSpreadsheet
            sourceWidget="DriftBars"
            clientId={clientId}
            // The calculation's own timestamp, not the click clock (TASK-825 made the
            // same distinction for the tile's own "as of" label).
            asOf={data?.timestamp ?? null}
            build={() => ({
              title: 'Drift by sub-asset class',
              sheetName: 'Drift',
              columns: [
                { header: 'Sub-asset class', width: 28 },
                { header: 'Current', fmt: 'percent', width: 12 },
                { header: 'Target', fmt: 'percent', width: 12 },
                // Percentage POINTS, not a percentage — a plain number, and the header
                // says so. Formatting a pp figure as a percent would be the same
                // category error as the one below, one unit further along.
                { header: 'Drift pp', width: 12 },
                { header: 'Outside band', width: 14 },
              ],
              // `fmt: 'percent'` is Excel's `0.0%`, which MULTIPLIES the stored cell by
              // 100 to display it. These figures are already 0-100 (the tile renders
              // `current_pct.toFixed(1)}%`), so writing them raw put `3520.0%` on a
              // handover sheet for a 35.2% allocation. Stored as the fraction Excel's
              // percent format expects, which also makes the cell arithmetically usable
              // by whoever receives it.
              rows: rows.map((r) => [
                r.sub_asset_class,
                r.current_pct / 100,
                r.target_pct / 100,
                r.drift_pp,
                r.breach ? 'yes' : 'no',
              ]),
            })}
          />
        ) : undefined
      }
    >
      {isLoading && (
        <div className="space-y-3">
          <div className="h-5 w-32 animate-pulse rounded bg-accent" />
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-6 w-full animate-pulse rounded bg-accent" />
          ))}
        </div>
      )}

      {isError && (
        <div className="text-[13px]">
          <p className="text-muted-foreground">Could not load drift — the client needs a risk profile.</p>
          <p className="mt-1 text-[11px] text-muted-foreground/70">
            {error instanceof Error ? error.message : String(error)}
          </p>
        </div>
      )}

      {data &&
        (() => {
          const breachCount = rows.filter((r) => r.breach).length;
          // Stable otherwise: the endpoint's ordering survives underneath the one row moved.
          const focusedRow = focusSac
            ? rows.find((r) => r.sub_asset_class === focusSac) ?? null
            : null;
          const orderedRows = focusSac
            ? [...rows].sort((a, b) =>
                (a.sub_asset_class === focusSac ? 0 : 1) - (b.sub_asset_class === focusSac ? 0 : 1))
            : rows;
          const maxDrift = Math.max(...rows.map((r) => Math.abs(r.drift_pp)), tolerance);
          return (
            <div>
              <div className="mb-3 flex items-center justify-between">
                <span className="text-[15px] font-semibold text-foreground">Drift vs Mandate</span>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-muted-foreground/70">±{tolerance}pp band</span>
                  {breachCount > 0 ? (
                    <span className="inline-flex items-center gap-1 rounded border border-destructive/20 bg-destructive/10 px-2 py-0.5 text-[11px] font-semibold text-destructive">
                      <AlertTriangle className="h-3 w-3" />
                      {breachCount} breach{breachCount !== 1 ? 'es' : ''}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded border border-emerald-500/20 bg-emerald-500/10 px-2 py-0.5 text-[11px] font-semibold text-emerald-600">
                      <Check className="h-3 w-3" />
                      All in band
                    </span>
                  )}
                </div>
              </div>

              {rows.length === 0 ? (
                <p className="text-[13px] text-muted-foreground">No allocation data for this client yet.</p>
              ) : (
                // TASK-832: the breaching SAC the advisor came from LEADS, and is marked. The
                // rest stay present — drift is only meaningful against the whole allocation —
                // but the advisor should not have to hunt for the row the alert was about.
                orderedRows.map((row) => (
                  <DriftBar
                    key={row.sub_asset_class}
                    row={row}
                    maxDrift={maxDrift}
                    tolerance={tolerance}
                    focused={row.sub_asset_class === focusSac}
                  />
                ))
              )}

              {/*
                TASK-832: the mandate's OWN answer to the breach.

                `portfolio_drift_breach` is the only source in the catalogue with
                `recommended_disposition="propose_trade"` (`signal_action_policy.py:198`), and
                the reason is written there: *"Drift against an agreed mandate is the one case
                where the corrective action is already defined by the mandate itself — the
                target weights are written down."* That proposal had nowhere to land. Here it
                does.
              */}
              {focusedRow && focusedRow.breach && (
                <DriftProposal
                  clientId={clientId}
                  row={focusedRow}
                  tolerance={tolerance}
                />
              )}

              <p className="mt-3 border-t border-border pt-3 text-[11px] text-muted-foreground/70">
                Source: CIO Mandate Strategy · Portfolio drift
              </p>
            </div>
          );
        })()}
    </WidgetContainer>
  );
}
